import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Edit, Plus, Receipt, Trash2 } from "lucide-react";

import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import Input from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import Loader from "../ui/loader";
import AmountEntryDialog, { AmountEntryValues } from "./AmountEntryDialog";
import ConfirmAlertDialog from "./ConfirmAlertDialog";
import { useToast } from "../../hooks/use-toast";
import {
  useAddOverhead,
  useDeleteOverhead,
  useOverheads,
  useUpdateOverhead,
} from "../../queries/overheads.queries";
import {
  OVERHEAD_CATEGORY_LABELS,
  Overhead,
  OverheadCategory,
} from "../../types";
import { formatAmount, formatMonth, formatShortDate } from "../../lib/utils";
import { getErrorMessage } from "../../utils/general/errors";

const PAGE_SIZE = 10;

const monthRange = (yearMonth: string) => {
  if (!yearMonth) return {};
  const [year, month] = yearMonth.split("-").map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return { from: `${yearMonth}-01`, to: `${yearMonth}-${String(lastDay).padStart(2, "0")}` };
};

const OverheadsManager: React.FC = () => {
  const { toast } = useToast();
  const [month, setMonth] = useState("");
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Overhead | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Overhead | null>(null);

  const { data, isLoading } = useOverheads({
    page,
    limit: PAGE_SIZE,
    ...monthRange(month),
  });
  const addOverhead = useAddOverhead();
  const updateOverhead = useUpdateOverhead();
  const deleteOverhead = useDeleteOverhead();

  const initialValues = useMemo<AmountEntryValues<OverheadCategory> | null>(
    () =>
      editing
        ? {
            category: editing.category,
            amount: editing.amount,
            expense_date: editing.expense_date,
            description: editing.description ?? undefined,
          }
        : null,
    [editing],
  );

  const handleSubmit = async (values: AmountEntryValues<OverheadCategory>) => {
    try {
      if (editing) {
        await updateOverhead.mutateAsync({ id: editing.id, ...values });
      } else {
        await addOverhead.mutateAsync(values);
      }
      toast({
        title: "Success!",
        description: editing ? "Overhead updated" : "Overhead added",
      });
      setDialogOpen(false);
      setEditing(null);
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to save overhead"),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteOverhead.mutateAsync(deleteTarget.id);
      toast({ title: "Success!", description: "Overhead removed" });
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to remove overhead"),
        variant: "destructive",
      });
    } finally {
      setDeleteTarget(null);
    }
  };

  const overheads = data?.overheads ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="flex items-end gap-2">
          <div>
            <Label>Filter by month</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
                setPage(1);
              }}
              className="w-48"
            />
          </div>
          {month && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setMonth("");
                setPage(1);
              }}
            >
              Clear
            </Button>
          )}
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Overhead
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            Total overhead {month ? `for ${formatMonth(month)}` : "(all time)"}
          </span>
          <span className="text-xl font-bold text-gray-900">
            {formatAmount(data?.totalAmount ?? 0)}
          </span>
        </CardContent>
      </Card>

      {isLoading ? (
        <Loader isSmall />
      ) : overheads.length === 0 ? (
        <div className="text-center py-12">
          <Receipt className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">No overheads recorded</h3>
          <p className="text-gray-500">
            Add business costs like rent, salaries and utilities here
          </p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="hidden sm:table-cell">Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {overheads.map((overhead) => (
                  <TableRow key={overhead.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatShortDate(overhead.expense_date)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {OVERHEAD_CATEGORY_LABELS[overhead.category] ?? overhead.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell max-w-[300px] truncate">
                      {overhead.description || "-"}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {formatAmount(overhead.amount)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Edit overhead"
                          onClick={() => {
                            setEditing(overhead);
                            setDialogOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Delete overhead"
                          onClick={() => setDeleteTarget(overhead)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      <AmountEntryDialog
        open={dialogOpen}
        title={editing ? "Edit Overhead" : "Add Overhead"}
        description="Record a business-wide cost not tied to a specific vehicle"
        categoryLabels={OVERHEAD_CATEGORY_LABELS}
        defaultCategory={OverheadCategory.RENT}
        initialValues={initialValues}
        isSubmitting={addOverhead.isPending || updateOverhead.isPending}
        onSubmit={handleSubmit}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
      />

      <ConfirmAlertDialog
        open={!!deleteTarget}
        title="Delete overhead"
        message="This overhead entry will be permanently removed."
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default OverheadsManager;
